import { type CountryCode, getCountryCallingCode } from 'libphonenumber-js/max';

/**
 * Represents a country option in the phone input component
 */
export type CountryOption = {
  /**
   * The name of the country
   */
  name: string;
  /**
   * The country iso code of the country
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  code: CountryCode | (string & {});
  /**
   * The dial code of the country
   */
  dialCode: string;
};

/**
 * Helper function to check if a string is a valid CountryCode
 */
export const isCountryCode = (code: string): code is CountryCode =>
  typeof code === 'string' && code !== '';

/**
 * Helper function to safely get a country calling code without throwing exceptions
 */
export const safeGetCountryCallingCode = (code: string): string => {
  try {
    if (isCountryCode(code)) {
      return `+${getCountryCallingCode(code)}`;
    }
  } catch (error) {
    // Silently handle invalid country codes
  }
  return '';
};

/**
 * Special mapping for unsupported country codes
 * These are country codes that are not supported by libphonenumber-js,
 * but exist in our Location Service and should map to specific dial codes
 */
export const CUSTOM_COUNTRY_CODE_MAP: Record<string, string> = {
  XA: '+7', // Crimea (uses Russia's dial code)
  XC: '+7', // Abkhazia (uses Russia's dial code)
  XY: '+90', // Northern Cyprus (uses Turkey's dial code)
};

/**
 * Maps custom country codes to their corresponding ISO CountryCode recognized by libphonenumber-js for formatting.
 */
export const CUSTOM_CODE_TO_ISO_CODE_MAP: Record<string, CountryCode> = {
  XA: 'RU', // Crimea should be formatted as Russian
  XC: 'RU', // Abkhazia should be formatted as Russian
  XY: 'TR', // Northern Cyprus should be formatted as Turkish
};

/**
 * Sanitizes dial codes by removing hyphens
 */
export const sanitizeDialCode = (dialCode: string) =>
  dialCode?.replace(/-/g, ' ') || '';

/**
 * Checks if the dial code string has a valid format (e.g., + followed by digits and optional hyphens).
 */
export const isValidDialCodeFormat = (dialCode: string): boolean => {
  if (typeof dialCode !== 'string') {
    return false;
  }
  // Starts with +, then a digit, then any number of digits or hyphens.
  return /^\+\d[\d-]*$/.test(dialCode);
};

/**
 * Gets the dial code from a CountryOption.
 * Priority:
 * 1. Custom map for special country codes.
 * 2. Provided `country.dialCode` if it's in a valid format.
 * 3. Derived from `country.code` using libphonenumber-js.
 * 4. Provided `country.dialCode` as a fallback if it exists (even if malformed and derivation failed).
 * 5. Empty string if none of the above yield a result.
 */
export const getDialCode = (country: CountryOption): string => {
  // 1. Check for special country codes first
  if (country.code && CUSTOM_COUNTRY_CODE_MAP[country.code]) {
    return CUSTOM_COUNTRY_CODE_MAP[country.code];
  }

  // 2. If country.dialCode is provided and has a valid format, use it.
  if (country.dialCode && isValidDialCodeFormat(country.dialCode)) {
    return country.dialCode;
  }

  // 3. Try to derive the dial code from the country code if valid.
  const derivedDialCode = safeGetCountryCallingCode(country.code);
  if (derivedDialCode) {
    return derivedDialCode;
  }

  // 4. Fallback to country.dialCode if it exists (even if it didn't pass isValidDialCodeFormat)
  //    and derivation failed.
  if (country.dialCode) {
    return country.dialCode;
  }

  return ''; // Absolute fallback
};
